import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Mail, 
  Phone, 
  Linkedin, 
  MapPin, 
  Calendar,
  Clock,
  Send,
  MessageCircle,
  Sparkles,
  Heart,
  Star,
  Zap,
  Coffee
} from "lucide-react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";

const floatingIcons = [
  { Icon: MessageCircle, size: 36, top: "8%", left: "5%", duration: 6, delay: 0, hideOnMobile: false },
  { Icon: Sparkles, size: 28, top: "15%", right: "8%", duration: 7, delay: 1, hideOnMobile: false },
  { Icon: Heart, size: 24, top: "45%", left: "3%", duration: 5.5, delay: 0.5, hideOnMobile: true },
  { Icon: Star, size: 32, top: "70%", right: "5%", duration: 8, delay: 2, hideOnMobile: false },
  { Icon: Zap, size: 26, top: "85%", left: "10%", duration: 6.5, delay: 1.5, hideOnMobile: true },
  { Icon: Coffee, size: 30, top: "30%", right: "3%", duration: 7.5, delay: 0.8, hideOnMobile: true },
  { Icon: Sparkles, size: 22, top: "60%", left: "8%", duration: 5, delay: 2.5, hideOnMobile: true },
  { Icon: Star, size: 40, top: "20%", left: "50%", duration: 6, delay: 1.2, hideOnMobile: false },
];

const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const contactInfo = [
    {
      icon: Mail,
      label: "E-Mail",
      value: "mk@ma4consult.de",
      href: "mailto:mk@ma4consult.de"
    },
    {
      icon: Phone,
      label: "Mobil",
      value: "0173 31 59 421",
      href: "tel:+4917331594421"
    },
    {
      icon: Linkedin,
      label: "LinkedIn",
      value: "Maria Keutel",
      href: "https://www.linkedin.com/in/maria-keutel-090a46aa/"
    },
    {
      icon: MapPin,
      label: "Standort",
      value: "Rosenheim, Bayern",
      href: null
    }
  ];

  const officeHours = [
    { day: "Montag - Donnerstag", time: "09:00 - 17:00 Uhr" },
    { day: "Freitag", time: "09:00 - 14:00 Uhr" },
    { day: "Samstag", time: "Nach Vereinbarung" },
    { day: "Sonntag", time: "Geschlossen" }
  ];

  return (
    <section id="contact" className="py-32 bg-gradient-subtle relative overflow-hidden">
      <div className="absolute inset-0 bg-dot-pattern opacity-[0.03]"></div>
      
      {/* Floating decorative icons */}
      {floatingIcons.map((item, index) => (
        <motion.div
          key={index}
          className={`absolute pointer-events-none text-accent/[0.07] ${item.hideOnMobile ? 'hidden md:block' : ''}`}
          style={{ top: item.top, left: (item as any).left, right: (item as any).right }}
          animate={{ y: [0, -15, 0] }}
          transition={{
            duration: item.duration,
            repeat: Infinity,
            ease: "easeInOut",
            delay: item.delay,
          }}
        >
          <item.Icon size={item.size} strokeWidth={1.5} />
        </motion.div>
      ))}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" ref={ref}>
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.h2 
            className="text-4xl md:text-5xl lg:text-6xl font-extrabold gradient-heading mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Kontakt
          </motion.h2>
          <motion.div 
            className="w-24 h-1 bg-gradient-to-r from-accent to-purple-accent mx-auto rounded-full mb-8"
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          ></motion.div>
          <motion.p 
            className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Lassen Sie uns ins Gespräch kommen. Ich freue mich darauf, 
            Sie kennenzulernen und gemeinsam an Ihren Zielen zu arbeiten.
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Card className="shadow-[var(--shadow-medium)] hover:shadow-[var(--shadow-large)] transition-all duration-300 mb-8 border-2 border-border/50 hover:border-accent/30 relative overflow-hidden group">
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-accent to-purple-accent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-foreground">
                    <Send className="w-5 h-5 text-accent" />
                    Kontaktinformationen
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-6">
                    {contactInfo.map((contact, index) => (
                      <motion.div 
                        key={index} 
                        className="flex items-center gap-4"
                        initial={{ opacity: 0, x: -20 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.3, delay: 0.5 + index * 0.1 }}
                        whileHover={{ x: 5 }}
                      >
                        <motion.div 
                          className="w-12 h-12 bg-gradient-to-br from-accent to-purple-accent rounded-xl flex items-center justify-center shadow-[var(--shadow-soft)]"
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          transition={{ duration: 0.2 }}
                        >
                          <contact.icon className="w-6 h-6 text-white" strokeWidth={2} />
                        </motion.div>
                        <div>
                          <p className="text-sm text-muted-foreground">{contact.label}</p>
                          {contact.href ? (
                            <a 
                              href={contact.href}
                              target={contact.href.startsWith('http') ? '_blank' : undefined}
                              rel={contact.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                              className="font-medium text-foreground hover:text-accent transition-[var(--transition-smooth)]"
                            >
                              {contact.value}
                            </a>
                          ) : (
                            <p className="font-medium text-foreground">{contact.value}</p>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Card className="bg-gradient-to-br from-primary to-primary/90 text-primary-foreground shadow-[var(--shadow-large)] hover:shadow-[var(--shadow-hover)] transition-all duration-300 relative overflow-hidden group">
                <div className="absolute inset-0 bg-dot-pattern opacity-10"></div>
                <motion.div 
                  className="absolute top-10 right-10 w-48 h-48 bg-purple-accent/20 rounded-full blur-3xl"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.2, 0.3, 0.2],
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
                <CardContent className="p-8 text-center relative">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Calendar className="w-12 h-12 mx-auto mb-4 opacity-90" />
                  </motion.div>
                  <h3 className="text-2xl md:text-3xl font-semibold mb-4">
                    Kostenloses Kennenlerngespräch
                  </h3>
                  <p className="mb-6 opacity-90 text-base md:text-lg">
                    In einem 30-minütigen Gespräch lernen wir uns kennen und besprechen, 
                    wie ich Sie bei Ihren Herausforderungen unterstützen kann.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button variant="cta" className="group bg-white text-primary hover:bg-white/90 shadow-[var(--shadow-large)] min-h-[44px]" asChild>
                      <a href="https://outlook.office365.com/book/Grndungsberatung@walbee.de/?ismsaljsauthenabled=true" target="_blank" rel="noopener noreferrer">
                        <Calendar className="mr-2 w-5 h-5" />
                        Zum Termin
                      </a>
                    </Button>
                    <Button variant="secondary" className="min-h-[44px]" asChild>
                      <a href="mailto:mk@ma4consult.de">
                        <Mail className="mr-2 w-5 h-5" />
                        E-Mail senden
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Card className="shadow-[var(--shadow-medium)] hover:shadow-[var(--shadow-large)] transition-all duration-300 border-2 border-border/50 hover:border-accent/30 relative overflow-hidden group">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-accent to-purple-accent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-foreground">
                  <Clock className="w-5 h-5 text-accent" />
                  Sprechzeiten
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {officeHours.map((hours, index) => (
                    <motion.div 
                      key={index} 
                      className="flex justify-between items-center py-2 border-b border-border last:border-b-0"
                      initial={{ opacity: 0, x: -10 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.3, delay: 0.6 + index * 0.05 }}
                    >
                      <span className="text-sm font-medium text-muted-foreground">
                        {hours.day}
                      </span>
                      <span className="text-sm font-medium text-foreground">
                        {hours.time}
                      </span>
                    </motion.div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-accent/5 rounded-lg border border-accent/20">
                  <p className="text-sm text-muted-foreground text-center">
                    <strong>Hinweis:</strong> Termine außerhalb der Sprechzeiten 
                    sind nach Absprache möglich.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <motion.div 
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.9 }}
        >
          <div className="inline-flex items-center gap-2 bg-card rounded-xl px-6 py-3 shadow-[var(--shadow-soft)] border border-border/50">
            <MapPin className="w-5 h-5 text-accent" />
            <span className="text-muted-foreground text-sm md:text-base">
              Beratung vor Ort im Raum Rosenheim oder deutschlandweit online
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
